import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { Metadata, RunView } from '@memberjunction/core';
import { UUIDsEqual } from '@memberjunction/global';
import { Subject } from 'rxjs';
import { mjBizAppsCommonPersonEntity } from '@mj-biz-apps/common-entities';
import { BCSaaSOrganizationPersonRoleEntity } from '@bluecypress/bcsaas-entities';
import { IzzyMacroEntity, IzzyTicketTeamEntity } from 'mj_generatedentities';
import { IAdminPanel, AdminPanelStatus } from '../admin-panel.interface';

type Scope = 'Personal' | 'Team' | 'Org';

interface MacroForm {
  name: string;
  content: string;
  contentFormat: 'HTML' | 'Text';
  fieldChanges: string;       // raw JSON text in the editor
  scope: Scope;
  ownerPersonId: string;
  teamId: string;
  status: 'Active' | 'Inactive';
}

interface MacroDisplay {
  macro: IzzyMacroEntity;
  isEditing: boolean;
  form: MacroForm;
}

/**
 * Macros admin panel (PRD v4.0 §11.6). Org-scoped CRUD over canned replies the ticket-detail composer
 * inserts via /macro. Each macro has Content (with {{placeholders}}), optional FieldChanges JSON, and
 * a Personal/Team/Org scope. Mirrors the other Administration panels.
 */
@Component({
  standalone: false,
  selector: 'izzy-macros',
  templateUrl: './macros.component.html',
  styleUrls: ['./macros.component.css'],
})
export class MacrosComponent implements OnInit, OnDestroy, IAdminPanel {
  @Input() organizationId: string | undefined;
  @Output() statusChange = new EventEmitter<AdminPanelStatus>();

  protected macros: MacroDisplay[] = [];
  protected people: mjBizAppsCommonPersonEntity[] = [];
  protected teams: IzzyTicketTeamEntity[] = [];

  protected readonly scopes: Scope[] = ['Org', 'Team', 'Personal'];

  protected isLoading = true;
  protected isSaving = false;
  protected errorMessage = '';
  protected successMessage = '';

  protected isCreating = false;
  protected createForm: MacroForm = this.emptyForm();

  private destroy$ = new Subject<void>();

  async ngOnInit(): Promise<void> { await this.loadData(); }
  ngOnDestroy(): void { this.destroy$.next(); this.destroy$.complete(); }

  private emptyForm(): MacroForm {
    return { name: '', content: '', contentFormat: 'HTML', fieldChanges: '', scope: 'Org', ownerPersonId: '', teamId: '', status: 'Active' };
  }

  protected async loadData(): Promise<void> {
    if (!this.organizationId) { this.macros = []; this.isLoading = false; return; }
    this.isLoading = true;
    this.errorMessage = '';
    try {
      await this.loadPeople();
      await this.loadTeams();
      const rv = new RunView();
      const res = await rv.RunView<IzzyMacroEntity>({
        EntityName: 'Macros',
        ExtraFilter: `OrganizationID = '${this.organizationId}'`,
        OrderBy: 'Name ASC',
        ResultType: 'entity_object',
      });
      const macros = res.Success ? res.Results : [];
      this.macros = macros.map(m => ({ macro: m, isEditing: false, form: this.toForm(m) }));
      this.statusChange.emit(
        this.macros.length === 0
          ? { status: 'none', tooltip: 'No macros' }
          : { status: 'good', tooltip: `${this.macros.filter(m => m.macro.Status === 'Active').length} active macro(s)` }
      );
    } catch (error) {
      console.error('Macros: failed to load', error);
      this.errorMessage = 'Failed to load macros.';
    } finally {
      this.isLoading = false;
    }
  }

  private async loadPeople(): Promise<void> {
    const rv = new RunView();
    const oprRes = await rv.RunView<BCSaaSOrganizationPersonRoleEntity>({
      EntityName: 'BC: Organization Person Roles',
      ExtraFilter: `OrganizationID = '${this.organizationId}'`,
      Fields: ['PersonID'],
    });
    const personIds = Array.from(new Set((oprRes.Success ? oprRes.Results : []).map(r => r.PersonID).filter(Boolean)));
    if (personIds.length === 0) { this.people = []; return; }
    const list = personIds.map(id => `'${id}'`).join(', ');
    const peopleRes = await rv.RunView<mjBizAppsCommonPersonEntity>({
      EntityName: 'MJ_BizApps_Common: People',
      ExtraFilter: `ID IN (${list})`,
      OrderBy: 'LastName ASC',
      ResultType: 'entity_object',
    });
    this.people = peopleRes.Success ? peopleRes.Results : [];
  }

  private async loadTeams(): Promise<void> {
    const rv = new RunView();
    const res = await rv.RunView<IzzyTicketTeamEntity>({
      EntityName: 'Ticket Teams',
      ExtraFilter: `OrganizationID = '${this.organizationId}' AND Status = 'Active'`,
      OrderBy: 'Name ASC',
      ResultType: 'entity_object',
    });
    this.teams = res.Success ? res.Results : [];
  }

  private toForm(m: IzzyMacroEntity): MacroForm {
    return {
      name: m.Name,
      content: m.Content || '',
      contentFormat: (m.ContentFormat as 'HTML' | 'Text') || 'HTML',
      fieldChanges: m.FieldChanges || '',
      scope: (m.Scope as Scope) || 'Org',
      ownerPersonId: m.OwnerPersonID || '',
      teamId: m.TeamID || '',
      status: (m.Status as 'Active' | 'Inactive') || 'Active',
    };
  }

  // ========== Display helpers ==========

  personName(personId: string | null): string {
    if (!personId) return '';
    const p = this.people.find(x => UUIDsEqual(x.ID, personId));
    if (!p) return 'Unknown';
    return p.DisplayName || `${p.FirstName || ''} ${p.LastName || ''}`.trim() || p.Email || 'Unknown';
  }
  teamName(teamId: string | null): string {
    if (!teamId) return '';
    return this.teams.find(t => UUIDsEqual(t.ID, teamId))?.Name || 'Unknown';
  }
  scopeLabel(m: IzzyMacroEntity): string {
    if (m.Scope === 'Personal') return `Personal · ${this.personName(m.OwnerPersonID)}`;
    if (m.Scope === 'Team') return `Team · ${this.teamName(m.TeamID)}`;
    return 'Org';
  }

  // ========== Create / Edit / Delete ==========

  startCreate(): void { this.isCreating = true; this.createForm = this.emptyForm(); }
  cancelCreate(): void { this.isCreating = false; }

  async saveCreate(): Promise<void> {
    if (!this.validate(this.createForm)) return;
    if (!this.organizationId) return;
    this.isSaving = true;
    try {
      const md = new Metadata();
      const m = await md.GetEntityObject<IzzyMacroEntity>('Macros');
      m.NewRecord();
      m.OrganizationID = this.organizationId;
      this.applyForm(m, this.createForm);
      if (!await m.Save()) {
        this.errorMessage = m.LatestResult?.CompleteMessage || 'Failed to create macro.';
        return;
      }
      await this.loadData();
      this.isCreating = false;
      this.showSuccess('Macro created');
    } catch (error) {
      console.error('Macros: create failed', error);
      this.errorMessage = 'Failed to create macro.';
    } finally {
      this.isSaving = false;
    }
  }

  startEdit(d: MacroDisplay): void { d.isEditing = true; d.form = this.toForm(d.macro); }
  cancelEdit(d: MacroDisplay): void { d.isEditing = false; }

  async saveEdit(d: MacroDisplay): Promise<void> {
    if (!this.validate(d.form)) return;
    this.isSaving = true;
    try {
      this.applyForm(d.macro, d.form);
      if (!await d.macro.Save()) {
        this.errorMessage = d.macro.LatestResult?.CompleteMessage || 'Failed to update macro.';
        return;
      }
      d.isEditing = false;
      this.showSuccess('Macro updated');
    } catch (error) {
      console.error('Macros: edit failed', error);
      this.errorMessage = 'Failed to update macro.';
    } finally {
      this.isSaving = false;
    }
  }

  async confirmDelete(d: MacroDisplay): Promise<void> {
    this.isSaving = true;
    try {
      if (!await d.macro.Delete()) {
        this.errorMessage = 'Failed to delete macro.';
        return;
      }
      this.macros = this.macros.filter(x => x !== d);
      this.showSuccess('Macro deleted');
    } catch (error) {
      console.error('Macros: delete failed', error);
      this.errorMessage = 'Failed to delete macro.';
    } finally {
      this.isSaving = false;
    }
  }

  // ========== Shared ==========

  private validate(form: MacroForm): boolean {
    if (!form.name.trim()) { this.errorMessage = 'Macro name is required.'; return false; }
    if (!form.content.trim()) { this.errorMessage = 'Macro content is required.'; return false; }
    if (form.scope === 'Personal' && !form.ownerPersonId) { this.errorMessage = 'Personal macros need an owner.'; return false; }
    if (form.scope === 'Team' && !form.teamId) { this.errorMessage = 'Team macros need a team.'; return false; }
    if (form.fieldChanges.trim()) {
      try { JSON.parse(form.fieldChanges); }
      catch { this.errorMessage = 'Field Changes must be valid JSON (e.g. {"Status":"Resolved"}).'; return false; }
    }
    this.errorMessage = '';
    return true;
  }

  private applyForm(m: IzzyMacroEntity, form: MacroForm): void {
    m.Name = form.name.trim();
    m.Content = form.content;
    m.ContentFormat = form.contentFormat;
    m.FieldChanges = form.fieldChanges.trim() || null;
    m.Scope = form.scope;
    // Keep owner/team consistent with scope.
    m.OwnerPersonID = form.scope === 'Personal' ? (form.ownerPersonId || null) : null;
    m.TeamID = form.scope === 'Team' ? (form.teamId || null) : null;
    m.Status = form.status;
  }

  private showSuccess(message: string): void {
    this.successMessage = message;
    setTimeout(() => { this.successMessage = ''; }, 3000);
  }
}

export function LoadMacrosComponent(): void {}
